// WHICH AGENT IS "THE LISTING AGENT" ON THIS CONTRACT?
//
// CONSUMER: shared (the merge-context builder + node-pool tests).
//
// The AAR forms name two agents: the buyer's and the seller's. Forty-five
// blanks across the reviewed forms carry one of those roles. The trap is
// that neither role names a fixed person — each one is US on one side of
// the deal and THE OTHER SIDE on the other:
//
//   On a LISTING, Stewardship represents the seller, so the listing
//   agent is our own advisor. Their licence, MLS code and firm block
//   come from the OS's own records — `licenses`, `firm_profiles`,
//   which is what migration 20260916005115 made possible.
//
//   On a PURCHASE, Stewardship represents the buyer, so the listing
//   agent is a competing brokerage's agent. Their details come from the
//   party row, filled by hand or read off the MLS sheet.
//
// Getting it backwards prints "Stewardship Realty LLC" on the seller's
// line of somebody else's contract — the exact mistake
// shared/realty/mls-agent-report.ts refuses to make at the other end of
// the same pipeline.
//
// THE THIRD ANSWER IS THE IMPORTANT ONE. A record whose side cannot be
// determined resolves to NEITHER source. Not "probably ours", not "try
// ours and fall back": a guess here is a wrong name on a signature page,
// and blank is what prints today. `realtySide()` already returns null
// when nothing says which side the deal is, and that null is carried
// through rather than defaulted away.
//
// Pure, so the rule is one testable statement rather than a condition
// repeated at each place a form field resolves.

import type { RealtySide } from "./transaction-side";

/** Where an agent role's values should be read from. */
export type AgentSource =
  /** Stewardship's own records: the advisor's licence row and the
   *  company's firm profile. */
  | "ours"
  /** The party roster on this record — the other brokerage's agent. */
  | "party"
  /** Nowhere. The side is unknown, so every one of these blanks prints
   *  empty rather than risking the wrong firm on a contract. */
  | "neither";

/** The two agent roles an AAR form names. */
export type ContractAgentRole = "listing_agent" | "buyer_agent";

/**
 * Which source fills this agent role on a record of this side.
 *
 * The whole rule, in one place:
 *
 * | side | listing agent | buyer's agent |
 * |------|---------------|---------------|
 * | sell | ours          | party         |
 * | buy  | party         | ours          |
 * | null | neither       | neither       |
 */
export function agentSourceFor(
  role: ContractAgentRole,
  side: RealtySide | null,
): AgentSource {
  if (side === null) return "neither";
  const weAreTheListingAgent = side === "sell";
  const roleIsListing = role === "listing_agent";
  return roleIsListing === weAreTheListingAgent ? "ours" : "party";
}

/**
 * Whom the broker named in the purchase contract's Agency Confirmation
 * (section 8q, line 449) represents.
 *
 * DELIBERATELY DERIVED, NOT STORED. The atlas proposed keeping "buyer"
 * in the offer-defaults policy row, and that would be wrong twice. It is
 * not a policy: whom our broker represents follows from which side of
 * the deal we are on, and storing it would be a second copy of a fact
 * this module already computes.
 *
 * And Stewardship does run dual agency, rarely (Grant, 2026-09-16). A
 * stored "buyer" would be quietly wrong a few times a year, on exactly
 * the deals where the agency disclosure matters most. So the rare case
 * is the agent's own call on the contract, and this returns the ordinary
 * answer rather than pretending to know about it.
 *
 * Null when the side is unknown, for the same reason as everything else
 * here: a guess on a signature page is worse than a blank.
 */
export function brokerAgencyConfirmation(
  side: RealtySide | null,
): "buyer" | "seller" | null {
  if (side === null) return null;
  // We represent the buyer on a purchase and the seller on a listing.
  // "Both" is dual agency and is never derived — the agent ticks it.
  return side === "buy" ? "buyer" : "seller";
}

/** Which party role holds the OTHER side's agent on a record of this
 *  side — the row `agentSourceFor` sends a "party" answer to.
 *
 *  Null when the side is unknown, for the same reason as above: reading
 *  the wrong party row is as bad as reading the wrong firm. */
export function otherSideAgentPartyRole(
  side: RealtySide | null,
): ContractAgentRole | null {
  if (side === null) return null;
  // On a sale we are the listing agent, so the other side is the buyer's.
  // These two strings are also the party roster's own role values
  // (TransactionPartyRole in shared/db/transaction-parties.ts), which is
  // why the return type is the same union rather than a parallel one.
  return side === "sell" ? "buyer_agent" : "listing_agent";
}
